import { cloneElement, useCallback, type Element } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';

import cssVariables from 'Lib/cssVariables';

type Props = {
  enter: string | number,
  exit: string | number,
  children: Element<*>,
  className: string,
  pageTransitionClassName: string,
  transitionKey: string,
};

const PageTransition = (props: Props) => {
  const { enter, exit, children, className, pageTransitionClassName, transitionKey } = props;

  const getChildFactory = useCallback(
    () =>
      (child: Element<CSSTransition>): Element<CSSTransition> => {
        // This is needed to get access to the props of the exiting nodes.
        // Since we don't know the exit animation until the new view component is passed in,
        // we need to set the exit animation on a node that is no longer in props.children,
        // but is accessible through TransitionGroup.
        const enableAnimation = !(enter === 'off' && exit === 'off');

        const childProps = {
          classNames: {
            enter: `animation-${enter}-enter`,
            enterActive: `animation-${enter}-enter-active`,
            exit: `animation-${exit}-exit`,
            exitActive: `animation-${exit}-exit-active`,
          },
          enter: enableAnimation,
          exit: enableAnimation,
        };

        return cloneElement(child, childProps);
      },
    [enter, exit]
  );

  const { pageTransitionDuration } = cssVariables;
  const wrapperClassName = classNames('H(100%)', className);
  const transitionClassName = classNames('H(100%)', pageTransitionClassName);
  const childFactory = getChildFactory();

  return (
    <div className={wrapperClassName}>
      <TransitionGroup component="div" className={transitionClassName} childFactory={childFactory}>
        <CSSTransition
          key={transitionKey}
          timeout={{
            enter: pageTransitionDuration,
            exit: pageTransitionDuration,
          }}
        >
          <div className="H(100%)">{children}</div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
};

PageTransition.defaultProps = {
  className: '',
  enter: false,
  exit: false,
  pageTransitionClassName: '',
};

export default PageTransition;
